import {groupBy, isArray, isPlainObject, isString, mapValues, flat, isNonNullish, sortBy} from 'remeda'
import {match} from 'ts-pattern'
import fastDeepEqual from 'fast-deep-equal'
import {Maybe, NotNullProps, Prettify} from './type'

/** a narrow type version of Object.keys */
export const keys = <T extends object>(o: T) => Object.keys(o) as (keyof T)[]
export const entries = <K extends string, V>(o: {[key in K]?: V}) => (o ? Object.entries(o) : []) as [K, V][]
export const fromEntries = <K extends string, V>(entries: [K, V][]) => Object.fromEntries(entries) as {[key in K]: V}
export const keyBy = <T, TKey extends string, TRes = T>(
  items: T[],
  by: (i: T) => TKey,
  v: (r: T) => TRes = (r: T) => r as any as TRes
): Record<TKey, TRes> => fromEntries(items.map(i => [by(i), v(i)])) as any as Record<TKey, TRes>

/**
 * Map's entries according to `fn` who's results are returned as an array.
 * e.g. `mapEntries({panda:{size:5},fish:{size:2}], (animal, name, i) => ({animal, name, rank:i+1}))` // returns [{animal:'panda', size:5, rank:1}, {animal:'fish', size:2, rank:2}]
 * This is like remeda's mapEntries, but with an index parameter.
 */
export const mapEntries = <T extends {[key: string]: any}, U>(
  group: T,
  fn: (key: keyof T, value: T[keyof T], i: number) => U
): U[] => (group ? entries(group).map(([key, value], i) => fn(key, value, i)) : [])

/**
 * Map's entries according to `fn` which returns key value pairs
 */
export const mapEntriesToObj = <K extends string, T, RK extends string, RV>(
  o: Record<K, T>,
  fn: (key: K, value: T, i: number) => [RK, RV]
) => fromEntries(mapEntries(o, fn)) as Record<RK, RV>

/**
 * Groups items by `getKey` then maps using getVal.
 * @example groupMap(
 *  [{cat:'Fruit', name:'Apple'},{cat:'Fruit', name:'Banana'},{cat:'Veggie', name:'Capsicum'}],
 *  k => k.cat,
 *  (g, cat) => ({cat, products:g})
 * ) // => [{cat:'Fruit', products:['Apple', 'Banana']}, {cat:'Veggie', products:['Capsicum']}]
 */
export const groupMap = <T, TVal, K extends string>(
  items: T[],
  getKey: (item: T) => K,
  getVal: (key: K, values: T[], i: number) => TVal
): TVal[] => (items ? mapEntries(groupBy(items, getKey), getVal as any) : [])

/**
 * Groups items by `getKey` then maps using getVal.
 * @example groupMapObj(
 *  [{cat:'Fruit', name:'Apple'},{cat:'Fruit', name:'Banana'},{cat:'Veggie', name:'Capsicum'}],
 *  k => k.cat,
 *  (g, cat) => ({products:g})
 * )
 * //returns {Fruit: {products:['Apple', 'Banana']}, Veggie:{products:['Capsicum']}}
 */
export const groupMapObj = <T, V, K extends string, TOptions extends {returnPartial?: boolean}>(
  items: T[],
  getKey: (item: T) => K,
  getVal: (values: T[], key: K) => V,
  options?: TOptions
): TOptions extends {partialKey: true} ? Partial<Record<K, V>> : Record<K, V> =>
  mapValues(groupBy(items, getKey) as Record<K, T[]>, getVal as any) as any

export const isNullOrEmpty = (v: any): v is null | undefined => !notNullOrEmpty(v)
export const notNullOrEmpty = <TValue>(v: TValue | null | undefined | {} | []): v is TValue =>
  v != null ?
    match(v)
      .when(isArray, a => a.length > 0)
      .when(isPlainObject, o => keys(o).length > 0)
      .when(isString, s => s != '')
      .otherwise(() => true)
  : false

/** Removes properties that have nullish values (shallow) */
export const compactObj = <T extends {[key: string]: any}>(o: T): NotNullProps<T> =>
  filterObj(o, (_, v) => v != null) as any

/** Removes properties that have nullish values (deeply) */
export const compactObjDeep = <T extends object>(o: T, options?: {onlyUndefined?: boolean}): T =>
  mapValues(
    filterObj(o, (_, v) => (options?.onlyUndefined ? v !== undefined : v != null)),
    v => {
      if (isPlainObject(v)) return compactObjDeep(v, options)
      if (isArray(v))
        return (v as any[])
          .filter(item => (options?.onlyUndefined ? item !== undefined : item != null))
          .map(item =>
            isPlainObject(item) ? compactObjDeep(item, options)
            : isArray(item) ? compactObjDeep(item, options)
            : item
          )
      return v
    }
  ) as any

/** Removes properties that don't match the given filter */
export const filterObj = <T extends {[key: string]: any}>(
  o: T,
  filter: (key: keyof T, value: T[keyof T]) => boolean
): Partial<T> => fromEntries(entries(o).filter(([k, v]) => filter(k, v))) as any
/** types from remeda for sorting */
declare const COMPARATORS: {
  readonly asc: <T>(x: T, y: T) => boolean
  readonly desc: <T>(x: T, y: T) => boolean
}
type OrderRule<T> = Projection<T> | readonly [projection: Projection<T>, direction: keyof typeof COMPARATORS]
type Projection<T> = (x: T) => Comparable
type Comparable =
  | ComparablePrimitive
  | {
      [Symbol.toPrimitive]: (hint: string) => ComparablePrimitive
    }
  | {
      toString: () => string
    }
  | {
      valueOf: () => ComparablePrimitive
    }
type ComparablePrimitive = bigint | boolean | number | string

export const sortObj = <T extends {[key: string]: any}>(o: T, sortParams: OrderRule<[keyof T, T[keyof T]]>) =>
  fromEntries(sortBy(entries(o), sortParams) as any) as T

/** non-null t2 values override t1 */
export const mergeTuples = <T extends K[], K>(t1: T, t2?: (K | undefined)[]) => t1.map((v, i) => t2?.[i] ?? v) as T

const treeFlatten = <R, C>(roots: R[], getChildren: (t: R | C) => C[] | undefined) => {
  const result: (R | C)[] = []
  const traverse = (t: R | C) => {
    result.push(t)
    getChildren(t)?.forEach(traverse)
  }
  roots.forEach(traverse)
  return result
}

type WithChildren<T> = T & {children: T[]}

/** provides common tree functions to an object graph that is already in a tree structure */
export const tree = <R extends {children: C[]}, C extends {children: C[]} = R['children'][number]>(roots: R[]) => {
  return {
    roots,
    /** array of all of the nodes */
    all: () => treeFlatten(roots, (n: R | C) => n.children),

    /** map all the notes in a flat list */
    flatMap: <U>(fn: (t: R | C) => U) => treeFlatten(roots, (n: R | C) => n.children).map(fn),

    /** update all the nodes in the same tree structure */
    treeMap: <U>(fn: (t: R | C) => U) => {
      const mapDeep = (t: R | C): WithChildren<U> => {
        const n = fn(t) as U
        return {...n, children: t.children.map(mapDeep) ?? []}
      }
      return roots.map(mapDeep)
    }
  }
}
export const deepEqual = fastDeepEqual

export const makeTree = <T extends {id: string}>(items: T[], getParentId: (t: T) => string | undefined) => {
  const byParentId = groupBy(items, i => getParentId(i) ?? '_')

  const buildTree = (parentId: string | undefined): Array<MakeTreeNode<T>> =>
    (byParentId[parentId ?? '_'] ?? []).map(item => ({
      ...item,
      children: buildTree(item.id)
    }))
  return buildTree(undefined)
}

export type MakeTreeNode<T> = T & {children: MakeTreeNode<T>[]}

/** concatenates any amount of nullish array's or items into a single array */
export const concat = <T>(...items: (Maybe<T>[] | Maybe<T>)[]): T[] =>
  flat(
    items.map(i =>
      Array.isArray(i) ? i
      : i == null ? []
      : [i]
    )
  ).filter(notNullOrEmpty)

export const mapWithPrev = <T, U>(
  items: T[],
  fn: (current: T, items: {prev: T | undefined; next: T | undefined}, i: number) => U
): U[] => items.map((item, i) => fn(item, {prev: items[i - 1], next: items[i + 1]}, i))

export const splitBy = <T>(items: T[], fn: (item: T, i: number) => boolean): [T[], T[]] => [
  items.filter(fn),
  items.filter((item, i) => !fn(item, i))
]

type NonNullableKeys<T> = {
  [K in keyof T]: T[K] extends null | undefined ? never : K
}[keyof T]
type NonNullableProperties<T, K extends keyof T> = {
  [P in keyof T]: P extends K ? NonNullable<T[P]> : T[P]
}

/** filters the array to items that are: not and null have each property given defined. Use when you need type inference */
export const compactByProps = <T extends object, K extends NonNullableKeys<T>>(
  items: Maybe<T>[],
  props: K[]
): Prettify<NonNullableProperties<T, K>>[] => items.filter(i => i != null && props.every(p => i[p] != null)) as any

export const prefixKeys = <T extends object, P extends string>(o: T, prefix: P): {[key in P & keyof T]: T[key]} =>
  fromEntries(entries(o).map(([k, v]) => [prefix + k, v])) as any
